import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  AllowedResultMimeTypes,
  PresignedResultUploadSchema,
  ProcedureResultListSchema,
  ProcedureResultSchema,
  UploadResultSchema,
  type PresignedResultUpload,
  type ProcedureId,
  type ProcedureResult,
  type ProcedureResultList,
  type UploadResultInput,
} from '@datazo/contracts';
import { apiFetch, ApiClientError } from '@/shared/api/client';

export const resultsKeys = {
  all: ['results'] as const,
  byProcedure: (id: ProcedureId) => ['results', 'procedure', id] as const,
};

export function useResultsByProcedure(
  procedureId: ProcedureId | undefined,
): UseQueryResult<ProcedureResultList, Error> {
  return useQuery({
    queryKey:
      procedureId !== undefined
        ? resultsKeys.byProcedure(procedureId)
        : (['results', 'procedure', '__missing__'] as const),
    queryFn: async () => {
      if (procedureId === undefined) throw new Error('procedureId requerido');
      return apiFetch<ProcedureResultList>(
        `/api/procedures/${encodeURIComponent(procedureId)}/results`,
        { parseAs: ProcedureResultListSchema },
      );
    },
    enabled: procedureId !== undefined,
    staleTime: 15_000,
  });
}

export interface UploadResultArgs {
  procedureId: ProcedureId;
  body: UploadResultInput;
}

export interface UploadFileArgs {
  procedureId: ProcedureId;
  file: File;
  descripcion?: string;
}

/** Mime types que acepta el backend (espejo de `AllowedResultMimeTypes`). */
type AllowedMime = (typeof AllowedResultMimeTypes)[number];

function isAllowedMime(value: string): value is AllowedMime {
  return (AllowedResultMimeTypes as readonly string[]).includes(value);
}

/**
 * Sube un archivo **directo al bucket** con URL prefirmada:
 *
 *   1. `presign`  — la API valida y firma la URL (no viaja el archivo)
 *   2. `PUT`      — el navegador sube directo al bucket (sin límite de body)
 *   3. `confirm`  — la API valida tamaño y magic bytes, y registra
 *
 * Si el backend responde que su almacenamiento no admite presign (caso del
 * disco local en desarrollo), cae automáticamente al endpoint clásico que manda
 * el archivo en base64. Así el mismo código funciona en dev y en producción.
 */
export function useUploadResultFile(): UseMutationResult<
  ProcedureResult,
  Error,
  UploadFileArgs
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ procedureId, file, descripcion }: UploadFileArgs) => {
      if (!isAllowedMime(file.type)) {
        throw new Error(`Tipo de archivo no permitido: ${file.type || 'desconocido'}`);
      }
      const mimeType: AllowedMime = file.type;
      const desc = descripcion?.trim();

      let presigned: PresignedResultUpload;
      try {
        presigned = await apiFetch<PresignedResultUpload>(
          `/api/procedures/${encodeURIComponent(procedureId)}/results/presign`,
          {
            method: 'POST',
            body: { nombreOriginal: file.name, mimeType, tamanoBytes: file.size },
            parseAs: PresignedResultUploadSchema,
          },
        );
      } catch (err) {
        // 409 = el almacenamiento no admite presign (disco local en dev).
        // Cualquier otro error (permiso, estado del episodio…) sí debe propagarse.
        if (err instanceof ApiClientError && err.status === 409) {
          return uploadViaApi(procedureId, file, mimeType, desc);
        }
        throw err;
      }

      // PUT directo al bucket. `fetch` a pelo: no lleva nuestra auth (la
      // credencial es la firma de la URL) y no debe pasar por `apiFetch`.
      const put = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: presigned.headers,
        body: file,
      });
      if (!put.ok) {
        throw new Error(
          `No se pudo subir el archivo al almacenamiento (HTTP ${put.status}). ` +
            'Si el problema persiste, reintenta en unos minutos.',
        );
      }

      return apiFetch<ProcedureResult>(
        `/api/procedures/${encodeURIComponent(procedureId)}/results/confirm`,
        {
          method: 'POST',
          body: {
            key: presigned.key,
            nombreOriginal: file.name,
            mimeType,
            ...(desc !== undefined && desc !== '' ? { descripcion: desc } : {}),
          },
          parseAs: ProcedureResultSchema,
        },
      );
    },
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: resultsKeys.byProcedure(vars.procedureId) });
    },
  });
}

/** Camino clásico: el archivo viaja en base64 dentro del body de la API. */
async function uploadViaApi(
  procedureId: ProcedureId,
  file: File,
  mimeType: AllowedMime,
  descripcion?: string,
): Promise<ProcedureResult> {
  const contenidoBase64 = await fileToBase64(file);
  return apiFetch<ProcedureResult>(
    `/api/procedures/${encodeURIComponent(procedureId)}/results`,
    {
      method: 'POST',
      body: {
        nombreOriginal: file.name,
        mimeType,
        contenidoBase64,
        ...(descripcion !== undefined && descripcion !== '' ? { descripcion } : {}),
      },
      parseAs: ProcedureResultSchema,
    },
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.readAsDataURL(file);
  });
}

export function useUploadResult(): UseMutationResult<
  ProcedureResult,
  Error,
  UploadResultArgs
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ procedureId, body }: UploadResultArgs) => {
      const validated = UploadResultSchema.parse(body);
      return apiFetch<ProcedureResult>(
        `/api/procedures/${encodeURIComponent(procedureId)}/results`,
        {
          method: 'POST',
          body: validated,
          parseAs: ProcedureResultSchema,
        },
      );
    },
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: resultsKeys.byProcedure(vars.procedureId) });
    },
  });
}

export function useDeleteResult(): UseMutationResult<
  void,
  Error,
  { resultId: string; procedureId: ProcedureId }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ resultId }) => {
      await apiFetch<{ ok: true }>(`/api/results/${encodeURIComponent(resultId)}`, {
        method: 'DELETE',
      });
    },
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: resultsKeys.byProcedure(vars.procedureId) });
    },
  });
}

/**
 * URL del endpoint de descarga del resultado. Exige JWT `Authorization: Bearer`
 * (permiso results:read), así que NO sirve para navegación directa (`<a href>`);
 * hay que traerlo con fetch autenticado y abrirlo como blob.
 */
export function resultDownloadUrl(resultId: string): string {
  return `/api/results/${encodeURIComponent(resultId)}/download`;
}
